<template>
  <Teleport to="#tooltip">
    <Transition
      :name="transitionName"
      leave-active-class="tooltip-leave-instant">
      <!-- style kept: the position is computed in pixels from the pointer or the anchor element -->
      <div
        v-if="visible"
        :id="TOOLTIP_PILL_ID"
        ref="tooltipEl"
        role="tooltip"
        :style="tooltipStyle"
        :class="[
          'shadow-dropdown z-tooltip bg-app-blue-950 dark:bg-elevated pointer-events-none fixed rounded-md text-white',
          variantClass,
          { 'tooltip-glide': isGliding },
        ]"
        v-html="safeText"></div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import DOMPurify from "dompurify";
import { storeToRefs } from "pinia";
import { TOOLTIP_PILL_ID, useTooltip } from "@stores/useTooltip";
import { useWindowSize } from "@vueuse/core";
import { resolveTooltipPosition } from "./tooltipPosition";

const tooltip = useTooltip();
const { visible, text, placement, variant, anchorRect, handedOff, mouseX, mouseY } = storeToRefs(tooltip);

/**
 * A shown tooltip is a snapshot: `anchorRect` is measured once at show time (tooltipDirective.ts) and
 * never re-measured, and a two-finger trackpad scroll fires no `mousemove` at all — the pointer's
 * viewport position is unchanged, only the page content moves under it. So a scroll left the pill
 * pointing at wherever its element used to be, for as long as the gesture continued: the ONLY thing
 * that ended it was the browser's post-scroll hover recompute finally issuing the missed `mouseleave`
 * once scrolling settled — "visible while scrolling, gone when you let go" (RTLCA-5620 follow-up).
 * Capture phase on `document`, since `scroll` does not bubble: this reaches every scrollable ancestor
 * in the page, not just the window, without a listener on each one.
 */
function hideOnScroll() {
  if (visible.value) tooltip.hideTooltip();
}

onMounted(() => document.addEventListener("scroll", hideOnScroll, { capture: true, passive: true }));
onUnmounted(() => document.removeEventListener("scroll", hideOnScroll, true));
// `includeScrollbar: false` — the pill must fit the *visible* width. `window.innerWidth` counts the
// vertical scrollbar, so clamping against it leaves the last few pixels of a right-edge tooltip
// sitting under the scrollbar gutter.
const { width: viewportWidth } = useWindowSize({ includeScrollbar: false });

const tooltipEl = ref<HTMLElement | null>(null);
const tooltipWidth = ref(0);
const tooltipHeight = ref(0);

const safeText = computed(() => {
  return DOMPurify.sanitize(text.value, {
    ALLOWED_ATTR: ["class", "style"],
  });
});

// `hint` wraps in regular weight; `label` is one semibold line. See TooltipVariant in the store.
const variantClass = computed(() =>
  variant.value === "label" ? "t-caption-bold px-px-12 py-px-6 whitespace-nowrap" : "t-caption p-px-8 max-w-xs"
);

// Cursor placements never glide: their position follows every pointer move, and easing it reads as lag.
const isGliding = computed(() => handedOff.value && placement.value !== "cursor" && placement.value !== "cursor-top");

// Anchored tooltips snap in from the side they point at, matching the navigation tooltips this renderer
// absorbed. Cursor-following ones appear instantly: a fade on every pointer move reads as lag, so they
// use the deliberately empty `tooltip-instant` transition rather than an undefined name.
const transitionName = computed(() => {
  if (placement.value === "right") return "tooltip-snap-right";
  if (placement.value === "bottom") return "tooltip-snap-down";
  return "tooltip-instant";
});

async function updateTooltipSize() {
  if (!visible.value) return;
  await nextTick();
  const rect = tooltipEl.value?.getBoundingClientRect();
  tooltipWidth.value = rect?.width ?? 0;
  tooltipHeight.value = rect?.height ?? 0;
}

// `viewportWidth` is in the list because the pill's own `maxWidth` is derived from it: a resize while
// a tooltip is on screen can rewrap it, and the clamping needs the new measurement.
watch([visible, safeText, placement, variant, viewportWidth], () => {
  void updateTooltipSize();
});

// All of the "does it still fit on screen" maths lives in `tooltipPosition.ts` so it can be unit
// tested without a browser — see `tooltipPosition.test.ts`.
const tooltipStyle = computed(() =>
  resolveTooltipPosition({
    placement: placement.value,
    anchor: anchorRect.value,
    pill: { width: tooltipWidth.value, height: tooltipHeight.value },
    cursor: { x: mouseX.value, y: mouseY.value },
    viewportWidth: viewportWidth.value || document.documentElement.clientWidth || 0,
  })
);
</script>
